// Shared order dialog element: address form, quote review, equipment review.
// Mirrors dialog#order-dialog in the approved HTML.
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

export interface OrderDialogProps {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  onConfirm?: (() => void) | null;
  onClose: () => void;
}

export function OrderDialog(props: OrderDialogProps): ReactNode {
  const { open, title, body, confirmLabel = 'Save preview', onConfirm, onClose } = props;
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    } else if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
  }, [open]);

  return (
    <dialog
      id="order-dialog"
      className="order-dialog"
      ref={dialogRef}
      aria-labelledby="dialog-title"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClose={onClose}
    >
      <h2 id="dialog-title">{title}</h2>
      <div id="dialog-body">{body}</div>
      <footer>
        <button id="dialog-close" type="button" onClick={onClose}>
          Close
        </button>
        <button
          id="dialog-confirm"
          type="button"
          className="confirm"
          hidden={!onConfirm}
          onClick={() => {
            if (onConfirm) onConfirm();
          }}
        >
          {confirmLabel}
        </button>
      </footer>
    </dialog>
  );
}
